/**
 * Summary-card data access. Reads the per-project summary_card_config and the
 * record's submissions, then delegates to the pure resolver. The admin config
 * UI for these rows lives in ../routes/summary-card-page.tsx.
 */
import { supabase } from '@/lib/supabase/client'
import {
  resolveSummaryValues,
  type SummaryFieldConfig,
  type SummarySubmission,
  type SummaryCardItem,
} from '../lib/resolve-summary'

export type { SummaryCardItem } from '../lib/resolve-summary'

function parseTitle(v: unknown): string {
  if (typeof v === 'string') return v
  if (v && typeof v === 'object') return (v as Record<string, string>).fa ?? ''
  return ''
}

export async function fetchSummaryCardConfig(projectId: string): Promise<SummaryFieldConfig[]> {
  const { data, error } = await supabase
    .from('summary_card_config')
    .select('*')
    .eq('project_id', projectId)
    .order('display_order')
  if (error) throw error
  return (data ?? []).map((c) => ({
    id: c.id,
    label: parseTitle(c.display_label),
    source_form_id: c.source_form_id,
    source_field_key: c.source_field_key,
    aggregation: c.aggregation,
    show_last_updated: c.show_last_updated,
    show_warning_indicator: c.show_warning_indicator,
    stale_threshold_days: c.stale_threshold_days,
    icon: c.icon,
    fallback_text: c.fallback_text ? parseTitle(c.fallback_text) : null,
    format_hint: c.format_hint,
  }))
}

/**
 * Resolve every configured summary field for one record. Fetches only the
 * submissions of the forms referenced by the config (single round-trip), then
 * runs the pure resolver. Returns [] when the project has no summary config.
 */
export async function fetchSummaryCard(
  projectId: string,
  recordId: string,
): Promise<SummaryCardItem[]> {
  const config = await fetchSummaryCardConfig(projectId)
  if (config.length === 0) return []

  const formIds = Array.from(new Set(config.map((c) => c.source_form_id)))
  const { data, error } = await supabase
    .from('submissions')
    .select('id, form_id, instance_index, field_values, updated_at, soft_warning_flags')
    .eq('record_id', recordId)
    .in('form_id', formIds)
    .eq('is_deleted', false)
  if (error) throw error

  const submissions = (data ?? []).map((s) => ({
    id: s.id,
    form_id: s.form_id,
    instance_index: s.instance_index ?? 1,
    field_values: (s.field_values as Record<string, unknown>) ?? {},
    updated_at: s.updated_at,
    soft_warning_flags: s.soft_warning_flags,
  })) as SummarySubmission[]

  return resolveSummaryValues(config, submissions)
}

/**
 * Batched summary resolution for a page of records — ONE submissions query for
 * all record IDs (not N per-record fetches). Used to show a primary summary
 * field (e.g. patient name) inline in the records list. Returns a map keyed by
 * record_id; records with no resolved values are simply absent.
 */
export async function fetchRecordsSummary(
  projectId: string,
  recordIds: string[],
): Promise<Map<string, SummaryCardItem[]>> {
  const result = new Map<string, SummaryCardItem[]>()
  if (recordIds.length === 0) return result

  const config = await fetchSummaryCardConfig(projectId)
  if (config.length === 0) return result

  const formIds = Array.from(new Set(config.map((c) => c.source_form_id)))
  const { data, error } = await supabase
    .from('submissions')
    .select('id, record_id, form_id, instance_index, field_values, updated_at, soft_warning_flags')
    .in('record_id', recordIds)
    .in('form_id', formIds)
    .eq('is_deleted', false)
  if (error) throw error

  const byRecord = new Map<string, SummarySubmission[]>()
  for (const s of data ?? []) {
    const sub: SummarySubmission = {
      id: s.id,
      form_id: s.form_id,
      instance_index: s.instance_index ?? 1,
      field_values: (s.field_values as Record<string, unknown>) ?? {},
      updated_at: s.updated_at,
      soft_warning_flags: s.soft_warning_flags,
    }
    const list = byRecord.get(s.record_id) ?? []
    list.push(sub)
    byRecord.set(s.record_id, list)
  }

  for (const recordId of recordIds) {
    result.set(recordId, resolveSummaryValues(config, byRecord.get(recordId) ?? []))
  }
  return result
}
